import {useState} from 'react';
import PropTypes from 'prop-types';
import {Box, Button, Paper, Stack, TextField, Typography} from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import useFileDownloader from '../../../../utility/hooks/useFileDownloader';
import ReportFilterDialog, {chosenCount, EMPTY_SETTINGS, filterPayload} from './ReportFilterDialog';

const CARD_STYLE = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    border: '1px solid #e0e0e0',
    borderRadius: 1,
    bgcolor: '#fff',
    p: 2,
    mt: 2
};

const PRIMARY_BUTTON = {
    backgroundColor: '#186BBE',
    boxShadow: 'none',
    '&:hover': {backgroundColor: '#155FA7', boxShadow: 'none'}
};

const SECONDARY_BUTTON = {
    color: '#186BBE',
    boxShadow: 'none',
    borderColor: '#186BBE'
};

function DateRangeReportCard({testId, icon, title, groupFilters, downloadCsv, downloadXlsx}) {
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [busy, setBusy] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [settings, setSettings] = useState(EMPTY_SETTINGS);
    const [strictRecipientsFiltering, setStrictRecipientsFiltering] = useState(false);
    const downloadReport = useFileDownloader();

    const chosen = chosenCount(settings);

    const handleDownload = async (downloadFn) => {
        setBusy(true);

        try {
            await downloadReport(downloadFn, {
                from: from || null,
                to: to || null,
                ...filterPayload(settings, strictRecipientsFiltering)
            });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Paper elevation={0} data-testid={testId} sx={CARD_STYLE}>
            {icon}

            <Typography variant="h6" sx={{mb: 3}}>{title}</Typography>

            <Stack direction={{xs: 'column', sm: 'row'}} gap={2} sx={{mb: 2}}>
                <TextField
                    label="From"
                    type="date"
                    size="small"
                    InputLabelProps={{shrink: true}}
                    data-testid={`${testId}-from`}
                    value={from}
                    onChange={(event) => setFrom(event.target.value)}
                />
                <TextField
                    label="To"
                    type="date"
                    size="small"
                    InputLabelProps={{shrink: true}}
                    data-testid={`${testId}-to`}
                    value={to}
                    onChange={(event) => setTo(event.target.value)}
                />
                <Button
                    variant="outlined"
                    startIcon={<FilterAltOutlinedIcon/>}
                    sx={SECONDARY_BUTTON}
                    data-testid={`${testId}-filters`}
                    onClick={() => setFiltersOpen(true)}
                >
                    Filters
                </Button>
            </Stack>

            {chosen > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                    {`${chosen} ${chosen === 1 ? 'filter' : 'filters'} chosen.`}
                </Typography>
            )}

            <Box sx={{display: 'flex', gap: 2}}>
                <Button
                    variant="contained"
                    disabled={busy}
                    sx={PRIMARY_BUTTON}
                    data-testid={`${testId}-download`}
                    onClick={() => handleDownload(downloadCsv)}
                >
                    CSV
                </Button>
                <Button
                    variant="outlined"
                    disabled={busy}
                    sx={SECONDARY_BUTTON}
                    data-testid={`${testId}-download-xlsx`}
                    onClick={() => handleDownload(downloadXlsx)}
                >
                    XLSX
                </Button>
            </Box>

            {filtersOpen && (
                <ReportFilterDialog
                    open={filtersOpen}
                    groupFilters={groupFilters}
                    settings={settings}
                    strictRecipientsFiltering={strictRecipientsFiltering}
                    onApply={(next, strict) => {
                        setSettings(next);
                        setStrictRecipientsFiltering(strict);
                    }}
                    onClose={() => setFiltersOpen(false)}
                />
            )}
        </Paper>
    );
}

DateRangeReportCard.propTypes = {
    testId: PropTypes.string.isRequired,
    icon: PropTypes.node.isRequired,
    title: PropTypes.string.isRequired,
    groupFilters: PropTypes.array,
    downloadCsv: PropTypes.func.isRequired,
    downloadXlsx: PropTypes.func.isRequired
};

export default DateRangeReportCard;
